import { useTranslation } from 'react-i18next'
import type { PulsKort, PulsResponse, PulsSammenligning } from './api'

/**
 * Markedspuls (#928 AC1/AC2): i går, uke hittil, måned hittil and hittil i år,
 * each against a like-for-like earlier window. Anchored on the last complete
 * day and always counting eierskifte, so neither the period picker nor the
 * omregistrering toggle moves it.
 */

interface Props {
  data: PulsResponse | null
  loading: boolean
  error: string | null
}

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}
const fmtShort = (iso: string) => fmtDate(iso).slice(0, 5)
const fmtInt = (n: number) => n.toLocaleString('nb-NO')
const fmtPct = (n: number) =>
  `${n > 0 ? '+' : ''}${n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`

// The card × comparison pairs the backend sends; listed so the typed t() sees
// real keys rather than every combination of the two unions.
const MOT_KEYS = {
  'i_gar.forrige_uke': 'oversikt.puls.mot.i_gar.forrige_uke',
  'i_gar.i_fjor': 'oversikt.puls.mot.i_gar.i_fjor',
  'uke.forrige_uke': 'oversikt.puls.mot.uke.forrige_uke',
  'uke.i_fjor': 'oversikt.puls.mot.uke.i_fjor',
  'maned.forrige_maned': 'oversikt.puls.mot.maned.forrige_maned',
  'maned.i_fjor': 'oversikt.puls.mot.maned.i_fjor',
  'hittil_i_ar.i_fjor': 'oversikt.puls.mot.hittil_i_ar.i_fjor',
} as const

function Change({ kort, s }: { kort: PulsKort; s: PulsSammenligning }) {
  const { t } = useTranslation('bruktbil')
  const label = t(MOT_KEYS[`${kort.id}.${s.mot}` as keyof typeof MOT_KEYS])
  const range = s.fra === s.til ? fmtDate(s.fra) : `${fmtDate(s.fra)}–${fmtDate(s.til)}`

  if (s.mangler_data || s.antall === null || s.endring === null) {
    return (
      <div className="flex items-baseline justify-between gap-2 text-xs" data-testid={`puls-${kort.id}-${s.mot}`}>
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        <span className="text-gray-400 dark:text-gray-500" title={t('oversikt.puls.manglerData', { range })}>–</span>
      </div>
    )
  }
  const up = s.endring > 0
  const down = s.endring < 0
  const color = up
    ? 'text-green-700 dark:text-green-400'
    : down ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'
  const arrow = up ? '▲' : down ? '▼' : '■'
  const value = s.endring_pct === null ? `${up ? '+' : ''}${fmtInt(s.endring)}` : fmtPct(s.endring_pct)
  return (
    <div
      className="flex items-baseline justify-between gap-2 text-xs"
      data-testid={`puls-${kort.id}-${s.mot}`}
      title={t('oversikt.puls.sammenligningTooltip', { range, antall: fmtInt(s.antall) })}
    >
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`font-medium whitespace-nowrap ${color}`}>
        <span aria-hidden="true">{arrow}</span> {value}
      </span>
    </div>
  )
}

export default function MarkedspulsStrip({ data, loading, error }: Props) {
  const { t } = useTranslation('bruktbil')

  return (
    <section className="space-y-2" data-testid="markedspuls">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t('oversikt.puls.tittel')}</h2>
        {data?.data_through && (
          <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="markedspuls-data-through">
            {t('oversikt.puls.dataTilOgMed', { date: fmtDate(data.data_through) })}
          </p>
        )}
      </div>

      {error !== null ? (
        <div className="card p-4 text-sm text-red-600 dark:text-red-400">
          {t('oversikt.feilVedLasting', { error: error || t('oversikt.ukjentFeil') })}
        </div>
      ) : loading || !data ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map(i => (
            <div key={i} data-testid="puls-skeleton" className="card p-4 animate-pulse h-32 bg-gray-100 dark:bg-dark-border rounded-lg" />
          ))}
        </div>
      ) : data.kort.length === 0 ? (
        <div className="card p-4 text-sm text-gray-500 dark:text-gray-400">{t('oversikt.puls.ingenData')}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.kort.map(kort => (
            <div key={kort.id} className="card p-4 space-y-2" data-testid={`puls-kort-${kort.id}`}>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {t(`oversikt.puls.kort.${kort.id}`)}
                </p>
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                  {kort.fra === kort.til ? fmtDate(kort.fra) : `${fmtShort(kort.fra)}–${fmtDate(kort.til)}`}
                </p>
              </div>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-testid={`puls-antall-${kort.id}`}>
                {fmtInt(kort.antall)}
              </p>
              <div className="space-y-1">
                {kort.sammenligninger.map(s => <Change key={s.mot} kort={kort} s={s} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="markedspuls-method">
        {t('oversikt.puls.metode')}
      </p>
    </section>
  )
}
